import {ExternalLinkIcon} from '@heroicons/react/outline';
import type {ReactNode} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';

import {Badge} from '@/components';
import {fileService, kycBucketKey} from '@/services/fileService';
import type {BusinessRFI} from '@/types';
import {BusinessRFIActorType, BusinessRFIStatus} from '@/types';
import * as formatDate from '@/utils/date';
import {renderFile} from '@/utils/imageFiles';
import {logRejection} from '@/utils/logRejection';
import {formatProvider} from '@/utils/merchant';

import BusinessRFIActions from './BusinessRFIActions';
import {getOutstandingDocuments, rfiStatusColorScheme} from './rfiStatus';

const SummaryRow = ({
  label,
  value,
  dataCy,
}: {
  label: string;
  value: ReactNode;
  dataCy?: string;
}) => (
  <div className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
    <p className="text-slate-500 text-sm dark:text-slate-100 shrink-0">
      {label}
    </p>
    <p
      data-cy={dataCy}
      className="text-sm font-medium text-gray-700 w-2/3 overflow-hidden dark:text-gray-100 break-words whitespace-normal text-wrap"
    >
      {value}
    </p>
  </div>
);

const BusinessRFISummary = ({
  item,
  businessName,
  dataCy,
}: {
  item: BusinessRFI;
  businessName: string;
  dataCy?: string;
}) => {
  const [fileUrls, setFileUrls] = useState<{[key: string]: string}>({});
  const documents = useMemo(() => item.documents || {}, [item.documents]);
  const outstanding = getOutstandingDocuments(item);
  const isDraft = item.status === BusinessRFIStatus.Draft;

  // [D2] Sign getObject URLs client-side from the stored keys (like KYCRequestedDocuments).
  useEffect(() => {
    const fetchAllUrls = async () => {
      const urls: {[key: string]: string} = {};
      for (const [docType, fileName] of Object.entries(documents)) {
        if (typeof fileName === 'string') {
          try {
            urls[docType] = await fileService.fetchUploadedFile(
              fileName,
              kycBucketKey,
            );
          } catch (error) {
            console.error(`Failed to fetch URL for ${docType}:`, error);
          }
        }
      }
      setFileUrls(urls);
    };
    // Each per-document fetch already catches, so this only fires if the loop
    // or `setFileUrls` itself throws; log it instead of leaving it unhandled.
    fetchAllUrls().catch(logRejection('Failed to resolve RFI document URLs'));
  }, [documents]);

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 py-5">
      <div className="flex items-center gap-4 mb-3">
        <h2
          data-cy={dataCy ? `${dataCy}-summary-title` : undefined}
          className="font-semibold text-xl mr-3"
        >
          Request For Information
        </h2>
        <Badge
          label={item.status}
          dataCy={dataCy ? `${dataCy}-summary-badge` : undefined}
          colorScheme={rfiStatusColorScheme(item.status)}
        />
      </div>

      <SummaryRow
        dataCy={dataCy ? `${dataCy}-summary-id` : undefined}
        label="RFI ID"
        value={item.id}
      />
      <SummaryRow
        label="For"
        dataCy={dataCy ? `${dataCy}-summary-for` : undefined}
        value={item.customerEmail || businessName || '—'}
      />
      {item.description ? (
        <SummaryRow
          dataCy={dataCy ? `${dataCy}-summary-description` : undefined}
          label="Description"
          value={item.description}
        />
      ) : null}
      {/* On a processor-raised request these three are the whole story: which
          payment is held, which rail holds it, and its own reason. */}
      {item.transactionId ? (
        <SummaryRow
          label="Transaction"
          value={
            <Link
              to={`/merchants/transactions/${item.transactionId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex gap-1 text-cyan-700 hover:underline"
            >
              <span>View transaction</span>
              <ExternalLinkIcon className="w-4 h-4 self-center" />
            </Link>
          }
        />
      ) : null}
      <SummaryRow
        dataCy={dataCy ? `${dataCy}-summary-created-by` : undefined}
        label="Created By"
        value={item.createdBy}
      />
      {item.processorMessage ? (
        <SummaryRow label="Processor reason" value={item.processorMessage} />
      ) : null}
      {/* Only a rail-closed request lets a later hold open a new one. When an
          admin closed it, a payment held again needs a manual request naming
          the transaction, so say which happened. */}
      {item.closedBy ? (
        <SummaryRow
          dataCy={dataCy ? `${dataCy}-summary-closed-by` : undefined}
          label="Closed by"
          value={
            item.closedBy === BusinessRFIActorType.Processor
              ? `${item.processor ? formatProvider(item.processor) : 'The rail'} (released the payment)`
              : 'Afriex (rail may still be holding)'
          }
        />
      ) : null}
      <SummaryRow
        label="Created"
        dataCy={dataCy ? `${dataCy}-summary-created` : undefined}
        value={
          item.createdAt
            ? formatDate.DDMonYYYY(String(item.createdAt), true)
            : '—'
        }
      />
      <SummaryRow
        label="Updated"
        dataCy={dataCy ? `${dataCy}-summary-updated` : undefined}
        value={
          item.updatedAt
            ? formatDate.DDMonYYYY(String(item.updatedAt), true)
            : '—'
        }
      />

      {/* Requested documents */}
      <div className="mt-6">
        <h4
          data-cy={
            dataCy ? `${dataCy}-summary-request-documents-title` : undefined
          }
          className="font-semibold text-gray-900 dark:text-gray-300 mb-2"
        >
          {'Requested Documents'.toLocaleUpperCase()}
        </h4>
        <hr className="mb-3 dark:border-dark-borderMid" />
        <p className="text-sm text-gray-700 dark:text-gray-100 mb-2">
          {item.requestedDocuments?.length
            ? item.requestedDocuments.join(', ')
            : 'None'}
        </p>
        {isDraft ? (
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            {item.processor ? formatProvider(item.processor) : 'The processor'}{' '}
            did not say which documents it needs. Use “Request documents” below
            to name them. The business is not notified until you do.
          </p>
        ) : null}
        {/* A draft has no ask yet, so it has nothing outstanding either, but
            that is not the same as being satisfied. Without this guard the
            success line printed directly under "did not say which documents it
            needs", which is the single line an operator scans to triage. */}
        {!isDraft &&
          (outstanding.length ? (
            <p className="text-sm text-failed-500 mb-3 font-medium">
              Outstanding: {outstanding.join(', ')}
            </p>
          ) : (
            <p className="text-sm text-successful-500 mb-3">
              All requested documents received.
            </p>
          ))}

        <div className="flex flex-wrap gap-1">
          {Object.keys(fileUrls).length > 0 ? (
            Object.entries(fileUrls).map(([docType, url]) => (
              <div
                data-cy={
                  dataCy ? `${dataCy}-summary-uploaded-documents` : undefined
                }
                key={docType}
                className="w-1/4 pr-2"
              >
                {renderFile(url, docType)}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No documents uploaded yet.
            </p>
          )}
        </div>
      </div>

      {/* Actions: resolve / request reupload / uploadRFIDoc */}
      <BusinessRFIActions rfi={item} dataCy={dataCy} />
    </div>
  );
};

export default BusinessRFISummary;
